import type { CatalogEntry } from '../types';

/**
 * Org purge (feature `23`) error codes + catalog. One route, guarded by the
 * billing module's `InternalTokenGuard` (`x-internal-api-token`; a rejection
 * throws `ErrorCode.Billing.Internal.Unauthorized`, not a code from this file):
 *
 * - `DELETE /internal/organizations/:orgId[?dryRun=true]` — hard-deletes an
 *   organization: every MySQL row, its ClickHouse warmup emails and its Redis state.
 *
 * Every code below is thrown BEFORE the MySQL transaction, so a failed call
 * never leaves a half-deleted org. `230103` is thrown after the mailboxes were
 * already torn down (soft-deleted), so a retry of the same call is the remedy —
 * it skips mailboxes that are already gone.
 *
 * It does NOT touch Paddle: cancel the
 * org's subscription separately before purging, or billing continues.
 *
 * Endpoint → `AA` map: purgeOrganization=01.
 */
export const ORG_PURGE_CODES = {
  /** purgeOrganization=01 (`DELETE /internal/organizations/:orgId`). */
  Purge: {
    /** No `organizations` row with this id. */
    OrganizationNotFound: '230101',
    /**
     * Another org's warmup history names one of this org's mailboxes as the
     * recipient, and a foreign key on that recipient column would force us to
     * delete the other org's rows. Nothing was changed.
     */
    CrossTenantHistoryBlocks: '230102',
    /** The email-worker didn't release a mailbox's live connection in time. */
    TeardownTimeout: '230103',
  },
} as const;

export const ORG_PURGE_CATALOG: Record<string, CatalogEntry> = {
  '230101': {
    httpStatus: 404,
    message: 'That organization does not exist.',
  },
  '230102': {
    httpStatus: 409,
    message:
      "Another organization's warmup history references this organization's mailboxes, so it can't be deleted without " +
      'removing their data. Nothing was changed.',
  },
  '230103': {
    httpStatus: 503,
    message: "Some mailboxes are still disconnecting. Nothing was hard-deleted yet — retry the call in a minute.",
  },
};
